import type {FabricObject} from '@postermywall/fabricjs-2';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import type {ItemRenderDecorationType} from '@PosterWhiteboard/items/item/item.types';

type RenderBase = (ctx: CanvasRenderingContext2D) => void;

export type ItemRenderPass = (ctx: CanvasRenderingContext2D, renderBase: RenderBase) => void;

export class ItemRenderDecorations {
  private readonly item: Item;
  private readonly passes = new Map<ItemRenderDecorationType, ItemRenderPass>();
  private baseRender: RenderBase | null = null;
  private patchedObject: FabricObject | null = null;
  private savedObjectCaching: boolean | null = null;

  public constructor(item: Item) {
    this.item = item;
  }

  public add(type: ItemRenderDecorationType, pass: ItemRenderPass): void {
    this.passes.set(type, pass);
    this.ensurePatched();
    this.invalidate();
  }

  public remove(type: ItemRenderDecorationType): void {
    this.passes.delete(type);
    if (this.passes.size === 0) {
      this.unpatch();
    }

    this.invalidate();
  }

  public reattach(): void {
    if (this.passes.size === 0) {
      return;
    }

    this.resetPatchState();
    this.ensurePatched();
  }

  public invalidate(): void {
    this.item.fabricObject.dirty = true;
    this.item.page.fabricCanvas.requestRenderAll();
  }

  private ensurePatched(): void {
    const fabricObject = this.item.fabricObject;
    if (this.patchedObject === fabricObject) {
      return;
    }

    // eslint-disable-next-line no-underscore-dangle -- patching fabric's private per-item render hook is the only per-item paint seam fabric exposes
    const baseRender = fabricObject._render.bind(fabricObject);
    this.baseRender = baseRender;
    this.patchedObject = fabricObject;
    // eslint-disable-next-line no-underscore-dangle -- see above
    fabricObject._render = (ctx: CanvasRenderingContext2D): void => {
      this.runStack(ctx, baseRender);
    };

    this.savedObjectCaching = fabricObject.objectCaching;
    fabricObject.objectCaching = false;
  }

  private unpatch(): void {
    if (this.patchedObject) {
      if (this.baseRender) {
        // eslint-disable-next-line no-underscore-dangle -- restoring fabric's private render hook patched in ensurePatched()
        this.patchedObject._render = this.baseRender;
      }

      if (this.savedObjectCaching !== null) {
        this.patchedObject.objectCaching = this.savedObjectCaching;
      }
    }

    this.resetPatchState();
  }

  private resetPatchState(): void {
    this.patchedObject = null;
    this.baseRender = null;
    this.savedObjectCaching = null;
  }

  private runStack(ctx: CanvasRenderingContext2D, baseRender: RenderBase): void {
    if (!this.isLiveEditorRender(ctx)) {
      baseRender(ctx);
      return;
    }

    let render = baseRender;
    for (const pass of this.passes.values()) {
      const inner = render;

      render = (c: CanvasRenderingContext2D): void => {
        pass(c, inner);
      };
    }

    render(ctx);
  }

  /**
   * Decorations are editor-only affordances. They must never paint into off-screen rasterization (thumbnails, page previews, downloads, video frames),
   * which render the item onto a throwaway canvas — so an active decoration would otherwise get baked into generated artifacts.
   */
  private isLiveEditorRender(ctx: CanvasRenderingContext2D): boolean {
    return ctx.canvas === this.item.page.fabricCanvas.lowerCanvasEl;
  }
}
